import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Lock, Loader2, Crown, Trash2, BarChart3 } from "lucide-react";
import { api, apiError, fileUrl } from "@/lib/api";
import { ImageUploader } from "@/components/ImageUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const emptyForm = () => ({ name: "", prize: "", prize_image: null, image_path: null, start_date: "", end_date: "", notify: true });

export function AdminPeriods({ vip = false }) {
  const [periods, setPeriods] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [viewRows, setViewRows] = useState(null);

  const openView = async (p) => {
    setViewing(p); setViewRows(null);
    const { data } = await api.get(`/admin/periods/${p.id}/leaderboard`);
    setViewRows(data.rows || []);
  };

  const load = async () => { const { data } = await api.get(`/admin/periods?vip=${vip ? 1 : 0}`); setPeriods(data); };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.id);
    setForm({ name: p.name, prize: p.prize || "", prize_image: p.prize_image, image_path: p.image_path, start_date: p.start_date?.slice(0, 10), end_date: p.end_date?.slice(0, 10) });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.start_date || !form.end_date) return toast.error("Nome, inizio e fine sono obbligatori.");
    setSaving(true);
    try {
      const payload = { ...form, is_vip: vip, notify: form.notify !== false };
      if (editing) await api.put(`/admin/periods/${editing}`, payload);
      else await api.post("/admin/periods", payload);
      toast.success(editing ? "Classifica aggiornata" : "Classifica creata");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const close = async (p) => {
    if (!window.confirm(`Chiudere "${p.name}"? La classifica verrà bloccata e i vincitori proclamati.`)) return;
    try {
      const { data } = await api.post(`/admin/periods/${p.id}/close`);
      toast.success(`Chiusa. Vincitori: ${(data.winners || []).map((w) => w.username).join(", ") || "nessuno"}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const del = async (p) => {
    if (!window.confirm(`Eliminare definitivamente la classifica "${p.name}"?`)) return;
    try { await api.delete(`/admin/periods/${p.id}`); toast.success("Classifica eliminata"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (periods === null) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-green-500" /></div>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-2xl font-extrabold uppercase text-zinc-950">Classifiche</h2>
        <Button data-testid="admin-create-period-button" onClick={openCreate} className="rounded-full bg-green-500 font-bold text-zinc-950 hover:bg-green-600">
          <Plus className="mr-1 h-4 w-4" /> Nuova
        </Button>
      </div>

      <div className="space-y-3">
        {periods.length === 0 && <p className="text-sm text-zinc-500">Nessuna classifica. Creane una.</p>}
        {periods.map((p) => (
          <div key={p.id} data-testid={`admin-period-${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                {p.image_path && <img src={fileUrl(p.image_path)} alt="" className="h-14 w-14 rounded-xl object-cover" />}
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-display text-lg font-bold uppercase text-zinc-950">{p.name}</p>
                    {p.status === "active"
                      ? <span className="rounded-full bg-green-500 px-2 py-0.5 text-xs font-black text-zinc-950">ATTIVA</span>
                      : <span className="rounded-full bg-slate-200 px-2 py-0.5 text-xs font-bold text-slate-600">CHIUSA</span>}
                  </div>
                  <p className="mt-1 text-sm text-zinc-500">Premio: <span className="font-bold text-green-600">{p.prize || "—"}</span></p>
                  <p className="text-xs text-zinc-400">{new Date(p.start_date).toLocaleDateString("it-IT")} – {new Date(p.end_date).toLocaleDateString("it-IT")} · {p.predictions_count} pronostici</p>
                  {p.status === "closed" && (p.winners || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {p.winners.map((w, i) => <span key={i} className="flex items-center gap-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-bold text-yellow-800"><Crown className="h-3 w-3" />{w.username} · {w.points}pt</span>)}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 gap-1">
                <Button data-testid={`admin-view-period-${p.id}`} onClick={() => openView(p)} size="sm" variant="ghost" className="text-green-600" title="Vedi classifica"><BarChart3 className="h-4 w-4" /></Button>
                <Button onClick={() => openEdit(p)} size="sm" variant="ghost" title="Modifica"><Pencil className="h-4 w-4" /></Button>
                {p.status === "active" && <Button data-testid={`admin-close-period-${p.id}`} onClick={() => close(p)} size="sm" variant="ghost" className="text-amber-600" title="Chiudi e proclama"><Lock className="h-4 w-4" /></Button>}
                <Button data-testid={`admin-delete-period-${p.id}`} onClick={() => del(p)} size="sm" variant="ghost" className="text-red-500" title="Elimina"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black uppercase">{editing ? "Modifica" : "Nuova"} classifica</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Nome classifica</Label>
              <Input data-testid="period-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Classifica Marcatori" className="rounded-xl" /></div>
            <ImageUploader label="Locandina classifica (opzionale)" value={form.image_path} onChange={(v) => setForm({ ...form, image_path: v })} testId="period-image" />
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Inizio</Label>
                <Input data-testid="period-start-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-xl" /></div>
              <div className="space-y-1.5"><Label>Scadenza</Label>
                <Input data-testid="period-end-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-xl" /></div>
            </div>
            <div className="rounded-2xl bg-slate-50 p-3">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-zinc-500">Premio (facoltativo, aggiungibile anche dopo)</p>
              <div className="space-y-1.5"><Label>Testo premio</Label>
                <Input data-testid="period-prize-input" value={form.prize} onChange={(e) => setForm({ ...form, prize: e.target.value })} placeholder="Es. PlayStation 5" className="rounded-xl" /></div>
              <div className="mt-3"><ImageUploader label="Locandina premio (opzionale)" value={form.prize_image} onChange={(v) => setForm({ ...form, prize_image: v })} testId="period-prize-image" /></div>
            </div>
          </div>
          <label className="mt-2 flex items-center gap-3 rounded-xl bg-slate-100 p-3">
            <Checkbox data-testid="period-notify-checkbox" checked={form.notify !== false} onCheckedChange={(v) => setForm({ ...form, notify: !!v })} />
            <span className="text-sm font-semibold text-zinc-700">Invia notifica push agli utenti</span>
          </label>
          <DialogFooter>
            <Button data-testid="period-save-button" onClick={save} disabled={saving} className="w-full rounded-full bg-zinc-950 font-bold text-green-400 hover:bg-zinc-800">
              {saving ? "Salvataggio..." : editing ? "Salva" : "Crea classifica"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl font-black uppercase">Classifica · {viewing?.name}</DialogTitle></DialogHeader>
          {viewRows === null ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-green-500" /></div>
          ) : viewRows.length === 0 ? (
            <p className="py-6 text-center text-sm text-zinc-400">Nessun punto assegnato in questa classifica.</p>
          ) : (
            <div data-testid="admin-period-leaderboard" className="overflow-hidden rounded-2xl border border-slate-200">
              <div className="grid grid-cols-[1.8rem_1fr_2.4rem_2.2rem_2.2rem_2.8rem] gap-1 bg-zinc-950 px-2 py-2 text-[9px] font-black uppercase text-green-400">
                <span>#</span><span>Giocatore</span><span className="text-center">Gioc</span><span className="text-center">✓</span><span className="text-center">✗</span><span className="text-right">Pt</span>
              </div>
              {viewRows.map((r) => (
                <div key={r.user_id} className="grid grid-cols-[1.8rem_1fr_2.4rem_2.2rem_2.2rem_2.8rem] items-center gap-1 border-b border-slate-100 px-2 py-2 text-sm last:border-0">
                  <span className="font-mono-t font-black text-zinc-400">{r.rank}</span>
                  <span className="truncate font-semibold text-zinc-900">{r.username}</span>
                  <span className="text-center font-mono-t text-zinc-600">{r.answered}</span>
                  <span className="text-center font-mono-t text-green-600">{r.correct}</span>
                  <span className="text-center font-mono-t text-red-500">{r.wrong}</span>
                  <span className="text-right font-mono-t font-black text-zinc-950">{r.points}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
